import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import {
  ThumbsUp,
  ThumbsDown,
  Send,
  Loader2,
  Check,
  MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../config';

const DocumentFeedback = ({ documentId, isPublic = false }) => {
  const [submitted, setSubmitted] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleFeedback = async (isHelpful) => {
    if (isHelpful) {
      // Submit immediately for positive feedback
      await submitFeedback(true, null);
    } else {
      // Show note input for negative feedback
      setSelectedFeedback(false);
      setShowNoteInput(true);
    }
  };

  const submitFeedback = async (isHelpful, feedbackNote) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/documents/${documentId}/feedback`, {
        is_helpful: isHelpful,
        note: feedbackNote
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('You have already submitted feedback for this article');
        setSubmitted(true);
      } else {
        toast.error('Failed to submit feedback');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitNote = async () => {
    await submitFeedback(false, note || null);
  };

  if (submitted) {
    return (
      <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" data-testid="feedback-submitted">
        <CardContent className="py-4">
          <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
            <Check className="w-5 h-5" />
            <span className="font-medium">Thank you for your feedback!</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700" data-testid="document-feedback">
      <CardContent className="py-4">
        {!showNoteInput ? (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Was this article helpful?
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFeedback(true)}
                disabled={submitting}
                className="hover:bg-green-50 hover:text-green-600 hover:border-green-300 dark:hover:bg-green-900/20 dark:hover:text-green-400"
                data-testid="feedback-helpful-button"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ThumbsUp className="w-4 h-4 mr-2" />
                )}
                Yes, helpful
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleFeedback(false)}
                disabled={submitting}
                className="hover:bg-red-50 hover:text-red-600 hover:border-red-300 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                data-testid="feedback-not-helpful-button"
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                No
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm font-medium">
                How can we improve this article?
              </span>
            </div>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Tell us what was missing or unclear... (optional)"
              className="min-h-[80px] resize-none"
              data-testid="feedback-note-input"
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSubmitNote}
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="feedback-submit-note-button"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Feedback
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowNoteInput(false);
                  setNote('');
                  setSelectedFeedback(null);
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentFeedback;
